import classNames from "classnames";
import { FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import useRealtime from "hooks/useRealtime";

import Card from "components/Card";

import HistoricalChartCard from "modules/assets/HistoricalChartCard";
import PageTemplate from "modules/common/PageTemplate";

import { HistoricalTrends } from "entities/assets";
import { AssetsApi } from "services/http";
import { PAGES } from "utils/links";

import { DEFAULT_PERIOD_LABEL, PERIODS, buildBanner, buildCharts } from "./data";

import styles from "./style.module.scss";

const STANDARD = "DM-HSD-GU119";

const ChartsPage: FC = () => {
  const { id } = useParams();
  const [activeLabel, setActiveLabel] = useState(DEFAULT_PERIOD_LABEL);
  const [trends, setTrends] = useState<HistoricalTrends | null>(null);

  const meta = PERIODS.find((item) => item.label === activeLabel) ?? PERIODS[1];

  const load = useCallback(() => {
    if (!id) return;

    AssetsApi.getHistoricalTrends(id, meta.key)
      .then(({ data }) => setTrends(data))
      .catch(() => setTrends(null));
  }, [id, meta.key]);

  useEffect(() => {
    load();
  }, [load]);

  useRealtime(["telemetry"], load, { assetId: Number(id) });

  const built = trends ? buildCharts(trends, meta) : null;
  const description = trends?.asset_name
    ? `${trends.asset_name} · Standard: ${STANDARD}`
    : `Standard: ${STANDARD}`;

  return (
    <PageTemplate
      isBackButton
      backButtonLink={id ? PAGES.ASSET_DETAIL.replace(":id", id) : undefined}
      title="Air quality historical data"
      description={description}
      className={styles.page}
    >
      <Card className={styles.card}>
        <div className={styles.ranges}>
          {PERIODS.map((item) => (
            <button
              key={item.label}
              type="button"
              className={classNames(styles.range, { [styles.rangeActive]: item.label === activeLabel })}
              onClick={() => setActiveLabel(item.label)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <p className={styles.banner}>{buildBanner(meta.interval)}</p>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Air quality parameters</h2>

          <div className={styles.airGrid}>
            {built?.airQuality.map((chart) => (
              <HistoricalChartCard key={chart.id} data={chart} />
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Thermal comfort</h2>

          <div className={styles.thermalGrid}>
            {built?.thermal.map((chart) => (
              <HistoricalChartCard key={chart.id} data={chart} />
            ))}
          </div>
        </section>
      </Card>
    </PageTemplate>
  );
};

export default ChartsPage;
